import React, { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
} from '@/components/ui/dropdown-menu';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Settings, 
  Download, 
  Upload, 
  Palette, 
  RotateCcw, 
  FileText,
  Sparkles,
  User,
  Building,
  BarChart3,
  AlertCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { useDashboardLayout, DASHBOARD_PRESETS, type PresetKey, type DashboardConfig } from '@/hooks/useDashboardLayout';
import { cn } from '@/lib/utils';

interface DashboardConfigManagerProps {
  className?: string;
}

const DashboardConfigManager: React.FC<DashboardConfigManagerProps> = ({ className }) => {
  const {
    applyPreset,
    exportConfig,
    importConfig,
    downloadConfig,
    resetLayout,
    presets,
  } = useDashboardLayout();

  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [importData, setImportData] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Ícones para cada preset
  const getPresetIcon = (presetKey: PresetKey) => {
    switch (presetKey) {
      case 'minimal':
        return <Sparkles className="h-4 w-4" />;
      case 'complete':
        return <BarChart3 className="h-4 w-4" />;
      case 'manager':
        return <Building className="h-4 w-4" />;
      case 'analyst':
        return <User className="h-4 w-4" />;
      default:
        return <Palette className="h-4 w-4" />;
    }
  };

  // Cores para cada preset
  const getPresetColor = (presetKey: PresetKey) => {
    switch (presetKey) {
      case 'minimal':
        return 'text-green-600';
      case 'complete':
        return 'text-blue-600';
      case 'manager':
        return 'text-purple-600';
      case 'analyst':
        return 'text-orange-600';
      default:
        return 'text-gray-600';
    }
  };

  // Importar arquivo
  const handleFileImport = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const content = e.target?.result as string;
          const config: DashboardConfig = JSON.parse(content);
          importConfig(config);
          setIsImportDialogOpen(false);
        } catch (error) {
          toast.error('Erro ao ler arquivo', {
            description: 'Arquivo de configuração inválido.',
          });
        }
      };
      reader.readAsText(file);
    }
  };

  // Importar via texto
  const handleTextImport = () => {
    try {
      const config: DashboardConfig = JSON.parse(importData);
      importConfig(config);
      setIsImportDialogOpen(false);
      setImportData('');
    } catch (error) {
      toast.error('Erro ao importar', {
        description: 'JSON inválido. Verifique a formatação.',
      });
    }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "h-10 px-3 gap-2 bg-white hover:bg-gray-50 border-gray-200",
              className
            )}
          >
            <Settings className="h-4 w-4" />
            <span className="hidden sm:inline">Configurações</span>
          </Button>
        </DropdownMenuTrigger>
        
        <DropdownMenuContent align="end" className="w-64">
          <DropdownMenuLabel className="text-xs font-medium text-gray-500 uppercase tracking-wide">
            Configurações do Dashboard
          </DropdownMenuLabel>
          
          <DropdownMenuSeparator />
          
          {/* Presets Predefinidos */}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger className="flex items-center gap-2">
              <Palette className="h-4 w-4" />
              <span>Aplicar Preset</span>
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent className="w-56">
              <DropdownMenuLabel className="text-xs text-gray-500">
                Configurações Predefinidas
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              {Object.entries(presets).map(([key, preset]) => (
                <DropdownMenuItem
                  key={key}
                  onClick={() => applyPreset(key as PresetKey)}
                  className="flex items-start gap-3 py-3"
                >
                  <div className={cn("mt-0.5", getPresetColor(key as PresetKey))}>
                    {getPresetIcon(key as PresetKey)}
                  </div>
                  <div className="flex-1">
                    <div className="font-medium text-gray-900">{preset.name}</div>
                    <div className="text-xs text-gray-500 mt-0.5">
                      {preset.description}
                    </div>
                  </div>
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          
          <DropdownMenuSeparator />
          
          {/* Exportar/Importar */}
          <DropdownMenuItem onClick={() => downloadConfig()} className="flex items-center gap-2">
            <Download className="h-4 w-4" />
            <span>Baixar Configuração</span>
          </DropdownMenuItem>
          
          <DropdownMenuItem onClick={handleFileImport} className="flex items-center gap-2">
            <Upload className="h-4 w-4" />
            <span>Importar Arquivo</span>
          </DropdownMenuItem>
          
          <Dialog open={isImportDialogOpen} onOpenChange={setIsImportDialogOpen}>
            <DialogTrigger asChild>
              <DropdownMenuItem 
                onSelect={(e) => e.preventDefault()}
                className="flex items-center gap-2"
              >
                <FileText className="h-4 w-4" />
                <span>Colar Configuração</span>
              </DropdownMenuItem>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Importar Configuração
                </DialogTitle>
                <DialogDescription>
                  Cole o JSON de configuração do dashboard abaixo para importar suas configurações.
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4">
                <div>
                  <Label htmlFor="config-json">Configuração JSON</Label>
                  <Textarea
                    id="config-json"
                    placeholder='{"version": "1.3.0", "cards": [...], ...}'
                    value={importData}
                    onChange={(e) => setImportData(e.target.value)}
                    className="min-h-[200px] font-mono text-sm"
                  />
                </div>
                
                {importData && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <div className="flex items-start gap-2 text-blue-700 text-sm">
                      <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="font-medium">Dica:</div>
                        <div>Certifique-se de que o JSON está válido antes de importar.</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsImportDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={handleTextImport}
                  disabled={!importData.trim()}
                >
                  Importar
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <DropdownMenuSeparator />
          
          {/* Reset */}
          <DropdownMenuItem 
            onClick={resetLayout}
            className="flex items-center gap-2 text-red-600 hover:text-red-700 hover:bg-red-50"
          >
            <RotateCcw className="h-4 w-4" />
            <span>Resetar para Padrão</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
      
      {/* Input file oculto */}
      <input
        ref={fileInputRef}
        type="file"
        accept=".json"
        onChange={handleFileChange}
        className="hidden"
      />
    </>
  );
};

export default DashboardConfigManager;
